import { useQuery } from "@tanstack/react-query";
import { create } from "zustand";
import BillServices from "@/services/billServices";
import { UserBill } from "@/types/bill";
import { getGlobalSessionId } from "@/services";

type BillsState = {
  bills: UserBill[];
  setBills: (_bills: UserBill[]) => void;
};

const useBillsStore = create<BillsState>((set) => ({
  bills: [],
  setBills: (bills) => set({ bills }),
}));

export const useBills = () => {
  const { setBills } = useBillsStore();
  const bills = useBillsStore((state) => state.bills);

  const sessionId = getGlobalSessionId();

  const query = useQuery({
    queryKey: ["bills"],
    enabled: !!sessionId,
    queryFn: async () => {
      try {
        const fetchedBills = await BillServices.getBills();
        setBills(fetchedBills);
        return fetchedBills;
      } catch (error) {
        console.error("Error fetching bills:", error);
        setBills([]);
        throw error;
      }
    },
  });

  const deleteBill = async (accountNumber: string, billingDate: string) => {
    try {
      const success = await BillServices.deleteBill(accountNumber, billingDate);
      if (success) {
        // Remove the bill from local state
        const updatedBills = bills.filter(
          (bill) =>
            bill.accountNumber !== accountNumber ||
            bill.billingDate !== billingDate
        );
        setBills(updatedBills);
        // Also refetch to ensure data consistency
        query.refetch();
      }
      return success;
    } catch (error) {
      console.error("Error deleting bill:", error);
      throw error;
    }
  };

  return {
    bills,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isRefetching: query.isRefetching,
    deleteBill,
  };
};
